<template>
  <div v-if="loading">Loading...</div>
  <div v-else>
    <template v-if="appointments.length">
      <v-card v-for="appointment in appointments" :key="appointment.appointmentId" class="mb-5">
        <v-card-text>
          <div class="d-flex align-center mb-2">
            <v-avatar size="70">
              <img :src="appointment.profilePicture" />
            </v-avatar>
            <div>
              <v-chip x-small dark :color="appointment.color" class="ml-4">{{
                appointment.status
              }}</v-chip>
              <div
                v-if="userType === 'student' && appointment.status === 'Finished' && !appointment.hasStudentSignature"
                class="sign-rate-cta signature-required-blink"
                @click="showDialog({ event: appointment })"
              >
                <v-icon small class="mr-1">mdi-draw</v-icon>
                <span>Sign & rate this booking</span>
                <v-icon x-small class="ml-1">mdi-chevron-right</v-icon>
              </div>
              <v-card-title class="headline py-0" v-if="userType === 'student'"
                @click="$router.push(`/instructor/${appointment.instructorId}`)">
                {{ appointment.name }}
              </v-card-title>
              <v-card-title v-else class="headline py-0" @click="$router.push(`/student/${appointment.studentId}`)">
                {{ appointment.name }}
              </v-card-title>

              <div class="ml-3">
                <v-chip x-small color="warning" class="ml-1" v-if="
                  appointment.type[0] === 'roadtest_rent_car' ||
                  appointment.type[0] === 'rentcar_n_lesson'
                ">
                  Road Test
                </v-chip>
                <v-chip x-small color="success" class="ml-1" v-if="
                  appointment.type[0] === 'booking_lesson' ||
                  appointment.type[0] === 'rentcar_n_lesson'
                ">
                  Lesson
                </v-chip>
              </div>
            </div>
          </div>
          <v-row align="end">
            <v-col :class="{ 'd-flex flex-wrap ': userType !== 'instructor' }" style="gap: 8px;">
              <p class="mb-0" v-if="userType === 'instructor'">
                <strong>Duration:</strong>
                {{
                  $moment
                    .duration(
                      $moment(appointment.end).diff($moment(appointment.start))
                    )
                    .asHours()
                }}hr
              </p>
              <p v-if="userType === 'instructor'">
                <strong>Location:</strong> {{ appointment.location }}
              </p>
              <v-btn small color="primary" @click="showDialog({ event: appointment })">
                Show Details
              </v-btn>
              <BookingModal v-model="showBookingModal" :key="bookingModalKey" :appointment="currentAppointmentDB"
                :remaining-hours="freeBookings - totalUtilizedHours" :payment-method-added="paymentMethodAdded"
                @submit="handleBookingSubmit" @error="(msg) => $toast.error(msg)" />
              <template v-if="userType !== 'instructor'">
                <!-- <v-btn small color="primary" @click="handleBookingClick(appointment)">
                  Add New Booking Lesson
                </v-btn> -->

                <!-- Payment Warning Dialog -->
                <v-dialog v-model="showPaymentWarning" max-width="500">
                  <v-card>
                    <v-card-title class="headline">Payment Required</v-card-title>
                    <v-card-text>
                      <v-alert type="warning" dense class="mb-4 text-center">
                        Please add payment method. You have used your free bookings.
                      </v-alert>
                      <div class="text-center">
                        <v-btn color="primary" @click="goToPayment">
                          Add Payment Method
                        </v-btn>
                        <v-btn text @click="showPaymentWarning = false" class="ml-2">
                          Cancel
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </template>
            </v-col>
            <v-col cols="auto" class="text-center">
              <Day :date="new Date(appointment.start)" />
              <small class="font-weight-bold warning--text" v-if="appointment.drivingLessonDates.length">
                + More
              </small>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
    <looper-fallback title="Your bookings is empty"
      message="Your inbox is currently empty, all future messages will appear here" v-else />
  </div>
</template>

<script>
import Day from '@/components/common/Day'
import BookingModal from '@/components/dashboard/BookingModal.vue'
import LooperFallback from '@/components/common/LooperFallback'
import Parse from 'parse'

export default {
  props: {
    loading: { type: Boolean, required: true },
    headers: { type: Array, required: true },
    appointments: { type: Array, required: true },
    getColor: { type: Function, required: true },
    showDialog: { type: Function, required: true }
  },
  components: {
    Day,
    LooperFallback,
    BookingModal
  },
  data () {
    return {
      showBookingModal: false,
      bookingModalKey: 0,
      currentAppointmentDB: null,
      currentStudent: null,
      sortBy: 'status',
      sortDesc: true,
      selected: [],
      userType: localStorage.getItem('userType'),
      freeBookings: 0,
      totalUtilizedHours: 0,
      paymentMethodAdded: false,
      showPaymentWarning: false
    }
  },
  computed: {
    canBookMoreLessons () {
      // Allow booking if:
      // 1. User hasn't used up free bookings OR
      // 2. Has payment method added
      return (
        this.totalUtilizedHours < this.freeBookings ||
        this.paymentMethodAdded
      )
    }
  },
  methods: {
    // async handleBookingClick (appointment) {
    //   // First check booking eligibility
    //   await this.checkBookingEligibility()
    //   if (this.canBookMoreLessons) {
    //     this.openBookingModal(appointment)
    //   } else {
    //     this.showPaymentWarning = true
    //   }
    // },
    goToPayment () {
      this.$router.push('/add-payment')
      this.showPaymentWarning = false
    },
    async openBookingModal (appointment) {
      // First check if user can book
      await this.checkBookingEligibility()
      if (!this.canBookMoreLessons) {
        this.$toast.error('Please add payment method. You have used your free bookings.')
        this.showPaymentWarning = true
        return
      }
      console.log('Opening booking modal for appointment:', appointment)
      console.log('appointment id:', appointment.appointmentId)
      const Appointments = Parse.Object.extend('Appointments')
      const query = new Parse.Query(Appointments)
      query.equalTo('objectId', appointment.appointmentId)
      try {
        const result = await query.first()
        if (result) {
          this.currentAppointmentDB = result
          console.log('Appointment DB data:', this.currentAppointmentDB)
        } else {
          this.currentAppointmentDB = null
          console.log('No appointment found in DB for this id.')
        }
      } catch (e) {
        this.currentAppointmentDB = null
        console.error('Error fetching appointment from DB:', e)
      }
      this.showBookingModal = true
      this.bookingModalKey++
    },
    async checkBookingEligibility () {
      try {
        const studentId = Parse.User.current().id
        const student = await new Parse.Query('_User').get(studentId)
        // Get package info
        const packageId = student.get('packageId')
        if (packageId) {
          const packageData = await new Parse.Query('SchoolPackages').get(packageId)
          this.freeBookings = parseInt(packageData?.get('freeBookings')) || 0
        }
        // Check payment method
        this.paymentMethodAdded = student.get('stripePaymentMethodAdded') || false
        // Calculate utilized hours
        this.totalUtilizedHours = await this.getUtilizedHours(studentId)
      } catch (error) {
        console.error('Error checking booking eligibility:', error)
      }
    },
    async getUtilizedHours (studentId) {
      try {
        const rows = await new Parse.Query('Appointments')
          .equalTo('studentId', new Parse.Object('_User', { id: studentId }))
          .containedIn('status', ['Finished', 'Approved'])
          .find()
        let totalHours = 0
        rows.forEach((item) => {
          totalHours += parseFloat(item.get('hoursLesson')) || 0
        })
        return totalHours
      } catch (error) {
        console.error('Error calculating utilized hours:', error)
        return 0
      }
    },
    async handleBookingSubmit (formData) {
      await this.checkBookingEligibility()
      if (!this.canBookMoreLessons) {
        this.$toast.error('Please add payment method. You have used your free bookings.')
        return
      }
      const lessonHours = parseFloat(formData.lessonHours || 1)
      const remainingFree = this.freeBookings - this.totalUtilizedHours
      if (lessonHours > remainingFree && !this.paymentMethodAdded) {
        this.$toast.error(
          `You only have ${Math.max(0, remainingFree)} free hour(s) remaining. Add a payment method or choose fewer hours.`
        )
        return
      }

      this.loading = true // Show loader
      const db = this.currentAppointmentDB
      const selectedDate = new Date(formData.date)
      const [startHour, startMinute] = formData.startTime.split(':').map(Number)
      // Start time
      const start = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        startHour,
        startMinute
      )
      // End time (+ lesson hours)
      const end = new Date(start.getTime() + lessonHours * 60 * 60 * 1000)
      const bookingData = {
        start,
        end,
        status: 'Pending',
        notes: formData.notes || '',
        appoinmentLocation: db.get('appoinmentLocation') || '',
        parking: db.get('parking') ?? false,
        highwayDriving: db.get('highwayDriving') ?? false,
        innerCityDrivingTurns: db.get('innerCityDrivingTurns') ?? false,
        roadtestOptions: db.get('roadtestOptions') || '',
        paid: db.get('paid') ?? false,
        paymentMethod: db.get('paymentMethod') || 'online',
        hoursLesson: String(lessonHours),
        drivingLessonDates: [],
        type: []
      }
      try {
        const Appointments = Parse.Object.extend('Appointments')
        const newBooking = new Appointments()
        Object.entries(bookingData).forEach(([key, value]) => {
          newBooking.set(key, value)
        })
        if (db.get('studentId')) {
          const studentPointer = new Parse.Object('_User')
          studentPointer.id = db.get('studentId').id
          newBooking.set('studentId', studentPointer)
        }
        if (db.get('instructorId')) {
          const instructorPointer = new Parse.Object('_User')
          instructorPointer.id = db.get('instructorId').id
          newBooking.set('instructorId', instructorPointer)
        }
        if (db.get('instructorPackageId')) {
          const instructorPackagePointer = new Parse.Object('InstructorPackages')
          instructorPackagePointer.id = db.get('instructorPackageId').id
          newBooking.set('instructorPackageId', instructorPackagePointer)
        }
        if (db.get('schoolPackageId')) {
          const schoolPackagePointer = new Parse.Object('SchoolPackages')
          schoolPackagePointer.id = db.get('schoolPackageId').id
          newBooking.set('schoolPackageId', schoolPackagePointer)
        }
        await newBooking.save()
        if (this.$toast && this.$toast.success) {
          this.$toast.success('Booking saved successfully!')
        }
        this.showBookingModal = false
        this.bookingModalKey++
        // Send sms notification to Instructor
        const instructorId = db.get('instructorId')?.id
        const studentId = db.get('studentId')?.id
        let instructorPhone = ''
        let studentName = ''
        let instructorName = ''
        try {
          if (instructorId) {
            const instructorQuery = new Parse.Query('_User')
            const instructor = await instructorQuery.get(instructorId)
            instructorPhone = instructor.get('phone') || ''
            const firstName = instructor.get('firstName') || ''
            const lastName = instructor.get('lastName') || ''
            instructorName = `${firstName} ${lastName}`.trim()
          }
          if (studentId) {
            const studentQuery = new Parse.Query('_User')
            const student = await studentQuery.get(studentId)
            const firstName = student.get('firstName') || ''
            const lastName = student.get('lastName') || ''
            studentName = `${firstName} ${lastName}`.trim()
          }
          const formattedDate = new Date(start).toLocaleString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })
          // Send SMS to instructor
          if (instructorPhone) {
            const smsData = {
              to: instructorPhone,
              message: `You have a new direct booking from ${studentName} on ${formattedDate}.`
            }
            console.log('📲 Sending SMS to instructor:', smsData)
            await Parse.Cloud.run('sendAppointmentSMS', smsData)
            if (this.$toast && this.$toast.success) {
              this.$toast.success('SMS sent to instructor.')
            }
            // Send sms to Admin
            const adminMessage = `New direct booking from ${studentName} on ${start.toLocaleString()} for instructor ${instructorName} with phone ${instructorPhone}.`
            console.log('📲 Sending SMS to Admin:', adminMessage)
            await Parse.Cloud.run('sendSMS', {
              message: adminMessage
            })
            console.log('Admin SMS successful')
            if (this.$toast && this.$toast.success) {
              this.$toast.success('SMS sent to admin.')
            }
          } else {
            console.warn('Instructor phone not found, SMS not sent.')
          }
        } catch (smsError) {
          console.error('Failed to fetch user info or send SMS:', smsError)
        }
        window.location.reload()
      } catch (error) {
        console.error('Error saving new booking:', error)
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Failed to save booking. Please try again.')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.sign-rate-cta {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 2px 6px rgba(245, 124, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.sign-rate-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.45);
}
.sign-rate-cta:active {
  transform: translateY(0);
}
.signature-required-blink {
  animation: signature-blink 1.5s ease-in-out infinite;
}
@keyframes signature-blink {
  0%, 100% { opacity: 1; box-shadow: 0 2px 6px rgba(245, 124, 0, 0.35); }
  50% { opacity: 0.88; box-shadow: 0 2px 10px rgba(245, 124, 0, 0.5); }
}
</style>
